/**
 * Donna habla: usa la voz que trae el navegador (gratis, sin enviar nada a ningún servicio). La calidad depende del equipo; se elige la mejor voz en español
 * (de preferencia latina y neuronal) y la persona puede cambiarla. Mientras Donna habla se avisa al resto (EV_HABLA) para que el micrófono no la escuche a ella.
 */
import { elegirVoz, textoParaVoz, trozosParaVoz, vocesEnEspanol } from "../../dominio/src/index.ts";

export const EV_HABLA = "donna:habla"; // detail: { activo: boolean }
const CLAVE = "donna.voz";

export interface AjustesVoz {
  /** Donna responde en voz alta a las órdenes por voz */
  habla: boolean;
  /** nombre de la voz elegida; vacío = la mejor del equipo */
  voz: string;
  velocidad: number;
  tono: number;
  /** no dice nombres de pacientes en voz alta */
  discreto: boolean;
  /** al entrar, saluda y cuenta cómo va el día (una vez al día) */
  saludar: boolean;
}
export const AJUSTES_POR_DEFECTO: AjustesVoz = { habla: true, voz: "", velocidad: 1, tono: 1, discreto: true, saludar: false };

export function leerAjustes(): AjustesVoz {
  try {
    const x = JSON.parse(localStorage.getItem(CLAVE) ?? "{}") as Partial<AjustesVoz>;
    return {
      habla: x.habla !== false, voz: typeof x.voz === "string" ? x.voz : "", velocidad: Math.min(1.3, Math.max(0.7, Number(x.velocidad) || 1)), tono: Number(x.tono) === 1.05 ? 1 : Math.min(1.4, Math.max(0.8, Number(x.tono) || 1)),   // 1,05 era el tono de la primera versión y sonaba forzado
      discreto: x.discreto !== false, saludar: x.saludar === true,
    };
  } catch { return { ...AJUSTES_POR_DEFECTO }; }
}
export function guardarAjustes(parcial: Partial<AjustesVoz>): AjustesVoz {
  const nuevos = { ...leerAjustes(), ...parcial };
  try { localStorage.setItem(CLAVE, JSON.stringify(nuevos)); } catch { /* sin almacenamiento: vale solo en esta sesión */ }
  return nuevos;
}

export const soportaHabla = () => typeof window !== "undefined" && "speechSynthesis" in window && typeof SpeechSynthesisUtterance !== "undefined";
const avisar = (activo: boolean) => window.dispatchEvent(new CustomEvent(EV_HABLA, { detail: { activo } }));

/** Las voces del equipo (algunos navegadores las cargan un instante después). */
export function vocesDisponibles(): Promise<SpeechSynthesisVoice[]> {
  if (!soportaHabla()) return Promise.resolve([]);
  const ya = speechSynthesis.getVoices();
  if (ya.length) return Promise.resolve(ya);
  return new Promise((ok) => {
    const fin = () => { speechSynthesis.removeEventListener("voiceschanged", fin); ok(speechSynthesis.getVoices()); };
    speechSynthesis.addEventListener("voiceschanged", fin);
    setTimeout(fin, 1500);
  });
}
export const vocesEspanolDelEquipo = async () => vocesEnEspanol(await vocesDisponibles());

let enCurso = 0;
/** Calla a Donna ahora mismo. */
export function callar() {
  enCurso++;
  if (soportaHabla()) { speechSynthesis.cancel(); avisar(false); }
}

/**
 * Dice el texto en voz alta. Devuelve true si la voz empezó a sonar. Sin `forzar`, solo habla si la persona dejó activada la voz de Donna.
 * Un texto nuevo corta al anterior.
 */
export async function hablar(texto: string, { forzar = false, ajustes = leerAjustes() }: { forzar?: boolean; ajustes?: AjustesVoz } = {}): Promise<boolean> {
  if (!soportaHabla() || (!ajustes.habla && !forzar)) return false;
  const trozos = trozosParaVoz(textoParaVoz(texto));
  if (!trozos.length) return false;
  const turno = ++enCurso;
  speechSynthesis.cancel();
  const voz = elegirVoz(await vocesDisponibles(), ajustes.voz);
  if (turno !== enCurso) return false;
  return new Promise<boolean>((resuelve) => {
    let empezo = false;
    trozos.forEach((t, i) => {
      const u = new SpeechSynthesisUtterance(t);
      if (voz) { u.voice = voz; u.lang = voz.lang; } else u.lang = "es-EC";
      u.rate = ajustes.velocidad; u.pitch = ajustes.tono;
      u.onstart = () => { if (!empezo) { empezo = true; avisar(true); resuelve(true); } };
      u.onend = () => { if (i === trozos.length - 1 && turno === enCurso) avisar(false); };
      u.onerror = () => { if (turno === enCurso) avisar(false); if (!empezo) resuelve(false); };
      speechSynthesis.speak(u);
    });
    // si el navegador no la deja hablar (todavía no hubo un toque en la página), no se queda esperando
    setTimeout(() => { if (!empezo) resuelve(false); }, 1500);
  });
}

/** Un número entre 0 y 1 para elegir entre las versiones de una frase. */
export const azar = () => Math.random();
